export const WORK_ORDER_FIELD_TYPES = [
  'short_text',
  'long_text',
  'number',
  'select',
  'yes_no',
  'multi_select',
  'date',
  'time',
  'datetime',
  'signature',
  'photo'
] as const;

export type WorkOrderFieldType = (typeof WORK_ORDER_FIELD_TYPES)[number];

export type WorkOrderFormField = {
  fieldId: string;
  label: string;
  type: WorkOrderFieldType;
  required?: boolean;
  options?: string[];
  placeholder?: string;
  helpText?: string;
};

export type WorkOrderFormSection = {
  key: string;
  title: string;
  fields: WorkOrderFormField[];
};

export type WorkOrderFormTemplate = {
  _id?: string;
  name: string;
  description?: string;
  status?: 'active' | 'archived';
  sections: WorkOrderFormSection[];
};

export type WorkOrderReportAnswer = {
  fieldId: string;
  sectionKey: string;
  label: string;
  type: WorkOrderFieldType;
  value?: unknown;
};

export type WorkOrderReport = {
  _id: string;
  workOrderId: string;
  templateId: string;
  templateSnapshot: WorkOrderFormTemplate;
  technicianId: string;
  technicianName?: string;
  reportDate: string;
  status: 'draft' | 'submitted';
  submittedAt?: string;
  answers: WorkOrderReportAnswer[];
  createdAt?: string;
  updatedAt?: string;
};

export const FIELD_TYPE_LABELS: Record<WorkOrderFieldType, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  number: 'Number',
  select: 'Dropdown',
  yes_no: 'Yes / No',
  multi_select: 'Checkboxes',
  date: 'Date',
  time: 'Time',
  datetime: 'Date & time',
  signature: 'Signature',
  photo: 'Photo'
};

export function newFieldId(): string {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function newSectionKey(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function flattenTemplateFields(template: Pick<WorkOrderFormTemplate, 'sections'>) {
  const fields: Array<WorkOrderFormField & { sectionKey: string; sectionTitle: string }> = [];
  for (const section of template.sections || []) {
    for (const field of section.fields || []) {
      fields.push({ ...field, sectionKey: section.key, sectionTitle: section.title });
    }
  }
  return fields;
}

function isAnswerFilled(type: WorkOrderFieldType, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (type === 'multi_select') return Array.isArray(value) && value.length > 0;
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'yes_no') return typeof value === 'boolean';
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function completionStats(template: Pick<WorkOrderFormTemplate, 'sections'>, answers: WorkOrderReportAnswer[]) {
  const fields = flattenTemplateFields(template);
  const required = fields.filter((f) => f.required);
  const answerMap = new Map(answers.map((a) => [`${a.sectionKey}:${a.fieldId}`, a]));
  let complete = 0;
  for (const field of required) {
    const answer = answerMap.get(`${field.sectionKey}:${field.fieldId}`);
    if (answer && isAnswerFilled(field.type, answer.value)) complete += 1;
  }
  return { complete, total: required.length };
}

export function answersFromTemplate(
  template: Pick<WorkOrderFormTemplate, 'sections'>,
  existing: WorkOrderReportAnswer[] = []
): WorkOrderReportAnswer[] {
  const map = new Map(existing.map((a) => [`${a.sectionKey}:${a.fieldId}`, a]));
  return flattenTemplateFields(template).map((field) => {
    const prev = map.get(`${field.sectionKey}:${field.fieldId}`);
    return {
      fieldId: field.fieldId,
      sectionKey: field.sectionKey,
      label: field.label,
      type: field.type,
      value: prev?.value
    };
  });
}

export function setAnswerValue(
  answers: WorkOrderReportAnswer[],
  sectionKey: string,
  fieldId: string,
  value: unknown
): WorkOrderReportAnswer[] {
  return answers.map((a) =>
    a.sectionKey === sectionKey && a.fieldId === fieldId ? { ...a, value } : a
  );
}

export function startOfLocalDay(date = new Date()): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function formatReportDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

export function defaultTemplate(): WorkOrderFormTemplate {
  return {
    name: 'New work order form',
    status: 'active',
    sections: [
      {
        key: newSectionKey(),
        title: 'Site Information',
        fields: [
          { fieldId: newFieldId(), label: 'Technician Name', type: 'short_text', required: true },
          { fieldId: newFieldId(), label: 'Time In', type: 'time', required: true },
          { fieldId: newFieldId(), label: 'Time Out', type: 'time', required: true }
        ]
      }
    ]
  };
}
